import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { PlusCircle, CarProfile, Trash, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_IMG = "https://images.pexels.com/photos/4116207/pexels-photo-4116207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/diagnostic");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this diagnostic session?")) return;
    try {
      await api.delete(`/diagnostic/${id}`);
      setItems((prev) => prev.filter((i) => i.diag_id !== id));
      toast.success("Session deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-mono-tech text-muted-foreground uppercase tracking-widest">// bay dashboard</div>
            <h1 className="font-display font-bold text-5xl tracking-tight">DIAGNOSTIC SESSIONS</h1>
            <p className="text-muted-foreground mt-2">Every case you've worked. Pick up where you left off or start fresh.</p>
          </div>
          <Button onClick={() => navigate("/diagnostic/new")} className="bg-primary hover:bg-primary/90 min-h-12" data-testid="new-diag-btn">
            <PlusCircle weight="bold" className="w-5 h-5 mr-2" /> New diagnostic
          </Button>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border border-border rounded-md p-5 bg-card h-40 scan-line" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState onNew={() => navigate("/diagnostic/new")} />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it, idx) => (
              <div
                key={it.diag_id}
                className="border border-border rounded-md bg-card p-5 hover:border-primary/50 transition-colors fade-up"
                style={{ animationDelay: `${idx * 40}ms` }}
                data-testid={`session-card-${it.diag_id}`}
              >
                <Link to={`/diagnostic/${it.diag_id}`} className="block">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center">
                        <CarProfile weight="bold" className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold leading-tight">
                          {it.vehicle?.year} {it.vehicle?.make} {it.vehicle?.model}
                        </div>
                        {it.vehicle?.vin ? (
                          <div className="text-[10px] font-mono-tech text-muted-foreground">VIN {it.vehicle.vin}</div>
                        ) : null}
                        {it.tech_name && (
                          <div className="text-[10px] font-mono-tech text-primary/80 mt-0.5">by {it.tech_name}</div>
                        )}
                      </div>
                    </div>
                    {it.done ? (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 font-mono-tech uppercase tracking-wider flex items-center gap-1">
                        <CheckCircle weight="bold" className="w-3 h-3" /> Solved
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-mono-tech uppercase tracking-wider flex items-center gap-1">
                        <Clock weight="bold" className="w-3 h-3" /> In progress
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-sm text-white/90 line-clamp-2">{it.complaint}</div>
                  {it.summary && (
                    <div className="mt-2 text-xs text-muted-foreground line-clamp-2 italic">{it.summary}</div>
                  )}
                </Link>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                  <div className="text-[10px] font-mono-tech text-muted-foreground">
                    {new Date(it.created_at).toLocaleString()}
                  </div>
                  <button
                    onClick={() => remove(it.diag_id)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`delete-session-${it.diag_id}`}
                  >
                    <Trash weight="bold" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="border border-border rounded-md overflow-hidden grid md:grid-cols-2">
      <div className="relative h-64 md:h-auto">
        <img src={EMPTY_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
      </div>
      <div className="p-10 flex flex-col justify-center">
        <div className="text-xs font-mono-tech text-muted-foreground">// no sessions yet</div>
        <div className="font-display text-3xl font-bold mt-1 tracking-tight">Start your first diagnostic</div>
        <p className="text-muted-foreground text-sm mt-3 max-w-md">
          Punch in a vehicle, describe the complaint, and let the AI narrow down the fault with you.
        </p>
        <Button onClick={onNew} className="mt-6 w-fit min-h-12 bg-primary hover:bg-primary/90" data-testid="empty-new-btn">
          <PlusCircle weight="bold" className="w-5 h-5 mr-2" /> New diagnostic
        </Button>
      </div>
    </div>
  );
}
